import { Box, VStack, HStack, Text, Icon, Badge, useColorModeValue } from '@ecoinvent/ui';
import { BiGlobe } from 'react-icons/bi';
import { FiArrowUpRight } from 'react-icons/fi';

import ProcessTypeIcon, { getProcessTypeBadgeStyle } from 'components/UI/ProcessTypeIcon';
import { FlowDetailSuppliers } from 'hooks/data/datasets/V4/flows/types';
import { formatNumericalValue } from 'utilities/numbers';

import { supplierTypeToIconBackground } from '../utils/constants';

import { LAYOUT_CONFIG } from './constants';
import { calculatePercentage } from './helpers';

type TooltipProps = {
  source: FlowDetailSuppliers;
  totalAmount: number;
  position: { x: number; y: number };
};

export const Tooltip = ({ source, totalAmount, position }: TooltipProps) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.700', 'gray.300');
  const tertiaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const progressBgColor = useColorModeValue('gray.200', 'gray.600');

  const badgeStyle = getProcessTypeBadgeStyle(source.type);
  const percentage = calculatePercentage(source.amount, totalAmount);
  const percentageDisplay = percentage.toFixed(0);

  return (
    <Box
      position="fixed"
      left={`${position.x + LAYOUT_CONFIG.tooltipOffset}px`}
      top={`${position.y + LAYOUT_CONFIG.tooltipOffset}px`}
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      shadow="lg"
      zIndex={1000}
      minW="320px"
      maxW="400px"
      pointerEvents="none"
      role="tooltip"
      aria-live="polite"
    >
      <VStack align="start" spacing={3}>
        <HStack spacing={2} w="full">
          <Badge
            bg={badgeStyle.bg}
            color={badgeStyle.iconColor}
            _dark={{ bg: badgeStyle.bg, color: badgeStyle.iconColor }}
            borderRadius="base"
            display="flex"
            minWidth="24px"
            maxWidth="24px"
            height="24px"
            flexShrink={0}
            p={0}
            alignItems="center"
            justifyContent="center"
          >
            <ProcessTypeIcon icon={badgeStyle.icon} subIcon={badgeStyle.subIcon} boxSize={5} subBoxSize={1.5} />
          </Badge>
          <Text fontSize="sm" color={secondaryTextColor} fontWeight="normal">
            {source.type}
          </Text>
          <Icon as={FiArrowUpRight} ml="auto" color={secondaryTextColor} boxSize={5} />
        </HStack>

        <Text fontSize="xl" fontWeight="bold" color={textColor} lineHeight="1.3">
          {source.name}
        </Text>

        <HStack spacing={2} align="baseline">
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            {formatNumericalValue(source.amount)} {source.unit}
          </Text>
          <Text fontSize="xl" fontWeight="normal" color={tertiaryTextColor}>
            {percentageDisplay}%
          </Text>
        </HStack>

        <Box w="full" h="8px" bg={progressBgColor} borderRadius="full" overflow="hidden">
          <Box
            h="full"
            w={`${percentage}%`}
            bg={(() => {
              const scheme = supplierTypeToIconBackground[source.type] || 'gray';
              return `${scheme}.400`;
            })()}
            transition="width 0.3s ease"
          />
        </Box>

        <HStack spacing={2}>
          <Icon as={BiGlobe} w={4} h={4} color={secondaryTextColor} />
          <Text fontSize="sm" color={secondaryTextColor} fontWeight="normal">
            {source.geography}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};
